import { useState, useEffect, useCallback } from 'react';
import {
  Plus, ReceiptText, Loader2, AlertTriangle, CheckCircle2, Clock,
  ChevronDown, ChevronUp, Trash2, RefreshCw, Pencil, X,
} from 'lucide-react';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../../foundation/altzero_base/base/components/ui/label';
import { Textarea } from '../../../../foundation/altzero_base/base/components/ui/textarea';
import { billService } from '../../services/billService';
import type { BvBill, BvBillFormData } from '../../types/billTypes';
import type { BvSite } from '../../types/billValidationTypes';

type Props = { siteId: string; site?: BvSite | null };

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU');
}

function fmtCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);
}

export const EMPTY: BvBillFormData = {
  site_id: '',
  bill_from: '',
  bill_to: '',
  peak_kwh: '',
  shoulder_kwh: '',
  off_peak_kwh: '',
  feed_in_kwh: '',
  peak_demand_kva: '',
  notes: '',
};

export function billToForm(bill: BvBill): BvBillFormData {
  return {
    site_id: bill.site_id,
    bill_from: bill.bill_from,
    bill_to: bill.bill_to,
    peak_kwh: bill.peak_kwh ?? '',
    shoulder_kwh: bill.shoulder_kwh ?? '',
    off_peak_kwh: bill.off_peak_kwh ?? '',
    feed_in_kwh: bill.feed_in_kwh ?? '',
    peak_demand_kva: bill.peak_demand_kva ?? '',
    notes: bill.notes ?? '',
  };
}

// ── Shared form fields (used by SiteBillsTab add/edit and BillDetailPage Usage tab) ──

export const UsageFormFields = ({
  f,
  onChange,
}: {
  f: BvBillFormData;
  onChange: (field: keyof BvBillFormData, value: string) => void;
}) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label>Usage From *</Label>
        <Input type="date" value={f.bill_from} onChange={e => onChange('bill_from', e.target.value)} required />
      </div>
      <div className="space-y-1.5">
        <Label>Usage To *</Label>
        <Input type="date" value={f.bill_to} onChange={e => onChange('bill_to', e.target.value)} required />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-1.5">
        <Label>Peak kWh</Label>
        <Input type="number" min="0" step="0.0001" placeholder="0" value={f.peak_kwh} onChange={e => onChange('peak_kwh', e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Shoulder kWh</Label>
        <Input type="number" min="0" step="0.0001" placeholder="0" value={f.shoulder_kwh} onChange={e => onChange('shoulder_kwh', e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Off-Peak kWh</Label>
        <Input type="number" min="0" step="0.0001" placeholder="0" value={f.off_peak_kwh} onChange={e => onChange('off_peak_kwh', e.target.value)} />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label>Feed-in kWh</Label>
        <Input type="number" min="0" step="0.0001" placeholder="0" value={f.feed_in_kwh} onChange={e => onChange('feed_in_kwh', e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>Peak Demand kVA</Label>
        <Input type="number" min="0" step="0.01" placeholder="0" value={f.peak_demand_kva} onChange={e => onChange('peak_demand_kva', e.target.value)} />
      </div>
    </div>
    <div className="space-y-1.5">
      <Label>Notes</Label>
      <Textarea placeholder="Optional notes about this usage period…" rows={2} value={f.notes ?? ''} onChange={e => onChange('notes', e.target.value)} />
    </div>
  </>
);

export function SiteBillsTab({ siteId, site }: Props) {
  const [bills, setBills] = useState<BvBill[]>([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<BvBillFormData>({ ...EMPTY, site_id: siteId });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Edit form
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<BvBillFormData | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Shadow generation
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  const loadBills = useCallback(async () => {
    const { success, data } = await billService.listBySite(siteId);
    if (success) setBills(data ?? []);
    setLoading(false);
  }, [siteId]);

  useEffect(() => { loadBills(); }, [loadBills]);

  // ── Add handlers ────────────────────────────────────────────────────────────

  const handleChange = (field: keyof BvBillFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.bill_from || !form.bill_to) {
      setFormError('Usage From and Usage To dates are required');
      return;
    }
    if (form.bill_from >= form.bill_to) {
      setFormError('Usage To must be after Usage From');
      return;
    }
    setSaving(true);
    const createRes = await billService.create(form);
    if (!createRes.success || !createRes.data) {
      setFormError(createRes.error ?? 'Failed to create usage entry');
      setSaving(false);
      return;
    }
    setSaving(false);
    setShowForm(false);
    setForm({ ...EMPTY, site_id: siteId });
    await loadBills();
  };

  // ── Edit handlers ────────────────────────────────────────────────────────────

  const handleEditStart = (bill: BvBill) => {
    setEditingId(bill.id);
    setEditForm(billToForm(bill));
    setEditError(null);
    setShowForm(false); // close add form if open
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm(null);
    setEditError(null);
  };

  const handleEditChange = (field: keyof BvBillFormData, value: string) => {
    setEditForm(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !editForm) return;
    setEditError(null);
    if (!editForm.bill_from || !editForm.bill_to) {
      setEditError('Usage From and Usage To dates are required');
      return;
    }
    if (editForm.bill_from >= editForm.bill_to) {
      setEditError('Usage To must be after Usage From');
      return;
    }
    setEditSaving(true);
    const res = await billService.updateUsage(editingId, editForm);
    if (!res.success || !res.data) {
      setEditError(res.error ?? 'Failed to update usage entry');
      setEditSaving(false);
      return;
    }
    setBills(prev => prev.map(b => b.id === editingId ? { ...b, ...res.data! } : b));
    setEditSaving(false);
    setEditingId(null);
    setEditForm(null);
  };

  // ── Other handlers ───────────────────────────────────────────────────────────

  const handleDelete = async (bill: BvBill) => {
    if (!confirm(`Delete usage entry for period ${fmtDate(bill.bill_from)} – ${fmtDate(bill.bill_to)}?`)) return;
    await billService.delete(bill.id);
    setBills((prev) => prev.filter((b) => b.id !== bill.id));
  };

  const handleGenerate = async (bill: BvBill) => {
    setGeneratingId(bill.id);
    try {
      const billWithSite = site ? { ...bill, site } : bill;
      const saved = await billService.recalculate(billWithSite);
      if (saved.success && saved.data) {
        const fresh = saved.data;
        setBills(prev => prev.map(b => b.id === bill.id ? { ...b, shadow_result: fresh.shadow_result, actual_total: fresh.actual_total } : b));
      } else {
        alert(saved.error ?? 'Failed to recalculate');
      }
    } catch { /* non-fatal */ }
    finally { setGeneratingId(null); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Shared form fields (used in both add + edit) ─────────────────────────────

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {bills.length} usage entr{bills.length !== 1 ? 'ies' : 'y'} for this site
        </p>
        <Button
          size="sm"
          variant={showForm ? 'outline' : 'default'}
          onClick={() => { setShowForm((v) => !v); setFormError(null); handleEditCancel(); }}
          className="flex items-center gap-1.5"
        >
          {showForm ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showForm ? 'Cancel' : 'Add Usage'}
        </Button>
      </div>

      {/* Inline add form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="border border-blue-200 rounded-xl p-5 bg-blue-50/30 space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">New Usage Entry</h4>
          <UsageFormFields f={form} onChange={handleChange} />
          {formError && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> {formError}
            </p>
          )}
          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={saving} className="flex items-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save Usage'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setFormError(null); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      {bills.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border border-dashed border-gray-200 rounded-xl">
          <ReceiptText className="h-10 w-10 mb-3 opacity-20" />
          <p className="text-sm font-medium">No usage entries for this site</p>
          <p className="text-xs mt-1">Click "Add Usage" to enter a billing period</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground font-medium uppercase tracking-wider border-b border-gray-100 bg-gray-50/50">
                <th className="px-5 py-3">Period</th>
                <th className="px-5 py-3">Days</th>
                <th className="px-5 py-3 text-right">Total kWh</th>
                <th className="px-5 py-3 text-right">Expected Total</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 w-52" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {bills.map((bill) => (
                <>
                  {/* Data row */}
                  <tr
                    key={bill.id}
                    className={editingId === bill.id ? 'bg-amber-50/40' : 'hover:bg-gray-50/50'}
                  >
                    <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                      {fmtDate(bill.bill_from)} – {fmtDate(bill.bill_to)}
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">
                      {bill.billing_days ?? '—'}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums">
                      {bill.total_kwh != null ? `${bill.total_kwh.toLocaleString()} kWh` : '—'}
                    </td>
                    <td className="px-5 py-4 text-right tabular-nums font-semibold">
                      {bill.shadow_result
                        ? fmtCurrency(bill.shadow_result.total)
                        : <span className="text-gray-300 font-normal">—</span>}
                    </td>
                    <td className="px-5 py-4">
                      {!bill.shadow_result ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          <Clock className="h-3 w-3" /> Pending
                        </span>
                      ) : (bill.shadow_result.warnings?.length ?? 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          <AlertTriangle className="h-3 w-3" /> {bill.shadow_result.warnings.length} warning{bill.shadow_result.warnings.length !== 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle2 className="h-3 w-3" /> Calculated
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => void handleGenerate(bill)}
                          disabled={generatingId === bill.id || editingId === bill.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-40"
                        >
                          {generatingId === bill.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <RefreshCw className="h-3.5 w-3.5" />}
                          {generatingId === bill.id
                            ? 'Generating…'
                            : bill.shadow_result ? 'Refresh' : 'Generate Expected Invoice'}
                        </button>
                        <button
                          onClick={() => editingId === bill.id ? handleEditCancel() : handleEditStart(bill)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title={editingId === bill.id ? 'Cancel edit' : 'Edit usage'}
                        >
                          {editingId === bill.id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => void handleDelete(bill)}
                          disabled={editingId === bill.id}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Inline edit row */}
                  {editingId === bill.id && editForm && (
                    <tr key={`${bill.id}-edit`} className="bg-amber-50/20">
                      <td colSpan={6} className="px-5 py-5">
                        <form onSubmit={handleUpdate} className="space-y-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Pencil className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
                              Editing usage entry
                            </span>
                          </div>
                          <UsageFormFields f={editForm} onChange={handleEditChange} />
                          {editError && (
                            <p className="text-sm text-red-600 flex items-center gap-1.5">
                              <AlertTriangle className="h-4 w-4" /> {editError}
                            </p>
                          )}
                          <div className="flex items-center gap-3 pt-1">
                            <Button type="submit" size="sm" disabled={editSaving} className="flex items-center gap-2">
                              {editSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                              {editSaving ? 'Saving…' : 'Save Changes'}
                            </Button>
                            <Button type="button" size="sm" variant="ghost" onClick={handleEditCancel}>
                              Cancel
                            </Button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
